import { Injectable } from '@angular/core';
import {
  UnifiedPropertyFinancials,
  UnifiedLoanFinancials,
  TemporalFinancialAmount
} from '@shared/definitions/unified-financial-data.model';
import { DataValidationService } from '@core/services/finance/data-validation.service';
import { DataNormalizationService } from '@core/services/finance/data-normalization.service';
import { FinancialCalculations } from '@core/utils/financial-calculations.util';

// Import existing proven services for real data aggregation
import { LoanAdapterService } from '@core/services/finance/loan-adapter.service';
import { TimePeriodService } from '@core/services/finance/time-period.service';
import { TimePeriod, DatePeriod as TimeDatePeriod } from '@shared/definitions/financial/time-period.model';

// Import raw data types
import { Property } from '@shared/definitions/property';
import { Contract } from '@shared/definitions/contract';
import { Loan } from '@shared/definitions/loan';

/**
 * STANDARDIZED KPI CALCULATOR SERVICE
 * 
 * Implements EXACT formulas from the Real Estate KPI Definitions document.
 * All calculations follow international real estate conventions and standards.
 * 
 * Key principles:
 * - Uses standardized variable definitions (Purchase Price, Net Operating Income, etc.)
 * - Implements international real estate calculation conventions
 * - Uses REAL DATA AGGREGATION instead of projections where possible
 * - Leverages existing proven services (LoanAdapterService, CashflowAggregationService)
 * - Provides yearly cashflow table generation based on actual contract and loan data
 * - Ensures consistent metric calculation across the application
 */
@Injectable({
  providedIn: 'root'
})
export class StandardizedKPICalculatorService {
  
  constructor(
    private dataValidationService: DataValidationService, 
    private dataNormalizationService: DataNormalizationService,
    private loanAdapterService: LoanAdapterService,
    private timePeriodService: TimePeriodService
  ) {}
  
  /**
   * BREAKEVEN RATIO: (Operating Expenses + Annual Debt Service) / Gross Rental Income.
   * Returns Infinity (rather than 100) when income is non-positive — a stricter
   * "cannot break even" signal than `FinancialCalculations.calculateBreakEvenOccupancy`.
   */
  private calculateBreakevenRatio(
    operatingExpenses: number,
    annualDebtService: number,
    grossRentalIncome: number
  ): number {
    if (grossRentalIncome <= 0) {
      console.warn('[StandardizedKPICalculatorService] Cannot calculate Breakeven Ratio: Gross rental income is zero or negative');
      return Number.POSITIVE_INFINITY;
    }
    return ((operatingExpenses + annualDebtService) / grossRentalIncome) * 100;
  }

  /**
   * Calculate comprehensive property KPIs using standardized formulas
   */
  calculatePropertyKPIs(
    propertyData: UnifiedPropertyFinancials,
    loanData: UnifiedLoanFinancials[]
  ): PropertyKPIResults {
    
    console.log(`[StandardizedKPICalculatorService] 🔍 Starting KPI calculation for property ${propertyData.propertyId}`);
    
    // Convert temporal amounts to consistent values
    let grossAnnualIncome = this.convertToAnnualAmount(propertyData.baseRent);
    
    // ✅ CRITICAL FIX: Use actualContractRent (strict mode - real contracts only)
    const actualGrossAnnualIncome = propertyData.actualContractRent 
      ? this.convertToAnnualAmount(propertyData.actualContractRent)
      : 0; // If no active contract, actual income = 0
    
    console.log(`[StandardizedKPICalculatorService] 📊 Income breakdown:`, {
      grossAnnualIncome: `€${grossAnnualIncome}` + (propertyData.baseRent.source === 'estimated' ? ' (estimated)' : ''),
      actualGrossAnnualIncome: `€${actualGrossAnnualIncome}` + (actualGrossAnnualIncome === 0 ? ' (no active contract)' : ' (real contract)')
    });
    
    const purchasePrice = propertyData.purchasePrice.amount;
    const currentValue = propertyData.currentValue.amount;
    
    // Track whether rent is estimated
    let isRentEstimated = false;
    
    // If no rental income but we have property value, estimate based on 5% yield
    // This ensures cashflow calculations are meaningful even without active contracts
    if (grossAnnualIncome === 0 && currentValue > 0) {
      const estimatedAnnualRent = currentValue * 0.05; // 5% annual yield
      grossAnnualIncome = estimatedAnnualRent;
      isRentEstimated = true;
      console.log(`[StandardizedKPICalculatorService] 💡 No rental income found, estimating based on property value:`, {
        currentValue: `€${currentValue.toLocaleString()}`,
        estimatedAnnualRent: `€${estimatedAnnualRent.toLocaleString()}`,
        yieldUsed: '5%'
      });
    }
    
    // Calculate total operating expenses (all converted to annual amounts)
    const totalOperatingExpenses = 
      this.convertToAnnualAmount(propertyData.propertyTax) +
      this.convertToAnnualAmount(propertyData.insurance) +
      this.convertToAnnualAmount(propertyData.maintenance) +
      this.convertToAnnualAmount(propertyData.managementFee) +
      this.convertToAnnualAmount(propertyData.utilities) +
      this.convertToAnnualAmount(propertyData.otherExpenses);
    
    // Calculate net income
    const vacancyLoss = grossAnnualIncome * (propertyData.vacancyRate / 100);
    const effectiveGrossIncome = grossAnnualIncome - vacancyLoss;
    const netOperatingIncome = effectiveGrossIncome - totalOperatingExpenses;
    
    // Calculate loan metrics
    const totalDebtService = loanData.reduce((total, loan) => {
      return total + this.convertToAnnualAmount(loan.regularPayment);
    }, 0);
    
    const totalLoanBalance = loanData.reduce((total, loan) => {
      return total + loan.currentBalance.amount;
    }, 0);
    
    // Calculate equity (current value - debt)
    const equity = currentValue - totalLoanBalance;
    
    // Calculate cash flow
    const netCashFlow = netOperatingIncome - totalDebtService;
    
    console.log(`[StandardizedKPICalculatorService] 📊 Cash flow calculation:`, {
      grossAnnualIncome: `€${grossAnnualIncome.toLocaleString()}`,
      effectiveGrossIncome: `€${effectiveGrossIncome.toLocaleString()}`,
      totalOperatingExpenses: `€${totalOperatingExpenses.toLocaleString()}`,
      netOperatingIncome: `€${netOperatingIncome.toLocaleString()}`,
      totalDebtService: `€${totalDebtService.toLocaleString()}`,
      netCashFlow: `€${netCashFlow.toLocaleString()}`,
      isEstimated: propertyData.baseRent.amount === 0 && grossAnnualIncome > 0
    });
    
    // Calculate KPIs. Yields use the acquisition-cost basis; fall back to the
    // current market value when no purchase price is on file, otherwise both
    // gross and net yield would read 0% even though the cap rate (on value) is
    // computed fine.
    const yieldBasis = purchasePrice > 0 ? purchasePrice : currentValue;
    const grossYield = FinancialCalculations.calculateGrossYield(grossAnnualIncome, yieldBasis);
    const netYield = FinancialCalculations.calculateNetYield(netOperatingIncome, yieldBasis);
    const capRate = FinancialCalculations.calculateCapRate(netOperatingIncome, currentValue);
    const expenseRatio = FinancialCalculations.calculateExpenseRatio(totalOperatingExpenses, grossAnnualIncome);
    const cashOnCashReturn = FinancialCalculations.calculateCashOnCashReturn(netCashFlow, equity);
    const breakevenRatio = this.calculateBreakevenRatio(totalOperatingExpenses, totalDebtService, grossAnnualIncome);
    const dscr = FinancialCalculations.calculateDSCR(netOperatingIncome, totalDebtService);
    const ltv = FinancialCalculations.calculateLTV(totalLoanBalance, currentValue);
    
    const kpis: PropertyKPIResults = {
      // Income metrics
      grossAnnualIncome,
      actualGrossAnnualIncome, // Add actual income (before estimation)
      effectiveGrossIncome,
      netOperatingIncome,
      vacancyLoss,
      netCashFlow,
      
      // Performance metrics
      grossYield,
      netYield,
      capRate,
      cashOnCashReturn,
      
      // Expense and debt metrics
      expenseRatio,
      breakevenRatio,
      dscr,
      ltv,
      
      // Financial structure
      totalOperatingExpenses,
      totalDebtService,
      totalLoanBalance,
      equity,
      
      // Metadata
      isRentEstimated, // Flag to indicate if rent is estimated
      dataQuality: this.assessDataQuality(propertyData, loanData),
      timestamp: new Date(),
      calculatedBy: 'StandardizedKPICalculatorService'
    };
    
    // Keep final result logging but make it more concise
    console.log(`[StandardizedKPICalculatorService] ✅ Final KPI Results for property ${propertyData.propertyId}:`, {
      capRate: `${kpis.capRate.toFixed(2)}%`,
      expenseRatio: `${kpis.expenseRatio.toFixed(2)}%`,
      netOperatingIncome: `€${kpis.netOperatingIncome.toLocaleString()}`,
      netCashFlow: `€${kpis.netCashFlow.toLocaleString()}`,
      dataQuality: kpis.dataQuality
    });
    
    return kpis;
  }
  
  /**
   * Assess the quality of the input data for KPI calculations
   */
  private assessDataQuality(
    propertyData: UnifiedPropertyFinancials,
    loanData: UnifiedLoanFinancials[]
  ): 'high' | 'medium' | 'low' {
    let qualityScore = 0;
    let maxScore = 0;
    
    // Check property data completeness
    maxScore += 3;
    if (propertyData.purchasePrice.amount > 0) qualityScore += 1;
    if (propertyData.currentValue.amount > 0) qualityScore += 1;
    if (propertyData.baseRent.amount > 0) qualityScore += 1;
    
    // Check expense data completeness
    maxScore += 2;
    const hasExpenseData = propertyData.propertyTax.amount > 0 || 
                          propertyData.insurance.amount > 0 || 
                          propertyData.maintenance.amount > 0;
    if (hasExpenseData) qualityScore += 2;
    
    // Check loan data completeness
    maxScore += 2;
    if (loanData.length > 0) qualityScore += 1;
    const hasCompleteLoanData = loanData.every(loan => 
      loan.originalAmount.amount > 0 && loan.regularPayment.amount > 0
    );
    if (hasCompleteLoanData) qualityScore += 1;
    
    const qualityRatio = qualityScore / maxScore;
    
    if (qualityRatio >= 0.8) return 'high';
    if (qualityRatio >= 0.5) return 'medium';
    return 'low';
  }
  
  /**
   * Generate yearly cashflow table using REAL DATA AGGREGATION
   * 
   * This method now uses the existing proven services to aggregate real data:
   * - Uses LoanAdapterService.calculateDebtService() for accurate loan payments 
   * - Leverages actual contract data for rental income
   * - Uses TimePeriodService for proper period generation
   * - Only fills gaps with projections where real data is unavailable
   */
  generateYearlyCashflowTable(
    propertyData: UnifiedPropertyFinancials,
    loanData: UnifiedLoanFinancials[],
    projectionYears = 10,
    rawLoans?: Loan[], // Raw loan data for real aggregation
    rawContracts?: Contract[], // Raw contract data for real aggregation
    rawProperty?: Property // Raw property data for context
  ): YearlyCashflowTable {
    
    const cashflowRows: CashflowRow[] = [];
    
    const currentYear = new Date().getFullYear();
    const startingYear = currentYear;
    
    console.log('[StandardizedKPICalculatorService] 📅 Generating cashflow table using REAL DATA AGGREGATION:', {
      propertyId: propertyData.propertyId,
      projectionYears,
      startingYear,
      currentYear,
      hasRawLoans: !!rawLoans,
      rawLoanCount: rawLoans?.length || 0,
      hasRawContracts: !!rawContracts,
      rawContractCount: rawContracts?.length || 0,
      hasRawProperty: !!rawProperty
    });

    // Year 0 - ACQUISITION (if applicable)
    if (rawProperty?.financials?.purchaseInfo?.date) {
      const purchaseDate = new Date(rawProperty.financials.purchaseInfo.date);
      const purchaseYear = purchaseDate.getFullYear();
      
      if (purchaseYear >= startingYear - 1) { // Show acquisition if recent
        const acquisitionRow = this.generateAcquisitionCashflowRow(propertyData, loanData, purchaseYear, rawProperty);
        cashflowRows.push(acquisitionRow);
      }
    }
    
    // Years 1-N - OPERATIONAL YEARS using real data aggregation
    for (let yearOffset = 0; yearOffset <= projectionYears; yearOffset++) {
      const calendarYear = startingYear + yearOffset;
      const yearStartDate = new Date(calendarYear, 0, 1); // January 1st
      const yearEndDate = new Date(calendarYear, 11, 31, 23, 59, 59); // December 31st
      
      // Create period for this year
      const yearPeriod: TimeDatePeriod = {
        startDate: yearStartDate,
        endDate: yearEndDate,
        type: TimePeriod.YEARLY,
        label: calendarYear.toString()
      };
      
      // Generate operational row using real data aggregation
      const operationalRow = this.generateRealDataCashflowRow(
        propertyData,
        loanData,
        yearOffset,
        calendarYear,
        yearPeriod,
        rawLoans,
        rawContracts,
        rawProperty
      );
      
      cashflowRows.push(operationalRow);
    }
    
    // Calculate cumulative cashflows
    let cumulativeCashflow = 0;
    cashflowRows.forEach(row => {
      cumulativeCashflow += row.netCashflow;
      row.cumulativeCashflow = cumulativeCashflow;
    });
    
    console.log('[StandardizedKPICalculatorService] ✅ Generated cashflow table with REAL DATA:', {
      totalRows: cashflowRows.length,
      firstYear: cashflowRows[0]?.yearLabel,
      lastYear: cashflowRows[cashflowRows.length - 1]?.yearLabel,
      sampleCashflows: cashflowRows.slice(0, 3).map(row => ({
        year: row.year,
        yearLabel: row.yearLabel,
        cashIn: row.cashInflows.total,
        cashOut: row.cashOutflows.total,
        debtService: row.cashOutflows.debtService,
        netCashflow: row.netCashflow,
        dataSource: row.notes?.includes('Real data') ? 'real' : 'projected'
      }))
    });
    
    return {
      propertyId: propertyData.propertyId,
      projectionYears,
      cashflowRows,
      summary: this.generateCashflowSummary(cashflowRows),
      generatedAt: new Date(),
      calculatedBy: 'StandardizedKPICalculatorService - Real Data Aggregation'
    };
  }
  
  /**
   * Convert temporal amount to annual equivalent
   */
  public convertToAnnualAmount(temporalAmount: TemporalFinancialAmount): number {
    switch (temporalAmount.period) {
      case 'monthly':
        return temporalAmount.amount * 12;
      case 'quarterly':
        return temporalAmount.amount * 4;
      case 'annually':
        return temporalAmount.amount;
      default:
        console.warn(`[StandardizedKPICalculatorService] Unknown period: ${temporalAmount.period}, treating as annual`);
        return temporalAmount.amount;
    }
  }
  
  /**
   * Calculate total acquisition costs using real property data
   */
  private calculateAcquisitionCosts(propertyData: UnifiedPropertyFinancials, rawProperty?: Property): number {
    // Try to get real acquisition costs from property data
    if (rawProperty?.financials?.purchaseInfo) {
      const purchaseInfo = rawProperty.financials.purchaseInfo;
      const realAcquisitionCosts = 
        (purchaseInfo.brokerCommission || 0) +
        (purchaseInfo.legalFees || 0) +
        (purchaseInfo.otherFees || 0);
      
      if (realAcquisitionCosts > 0) {
        console.log('[StandardizedKPICalculatorService] 📄 Using real acquisition costs:', {
          brokerCommission: purchaseInfo.brokerCommission || 0,
          legalFees: purchaseInfo.legalFees || 0,
          otherFees: purchaseInfo.otherFees || 0,
          total: realAcquisitionCosts
        });
        return realAcquisitionCosts;
      }
    }
    
    // Fallback to percentage estimation
    const estimatedCosts = propertyData.purchasePrice.amount * 0.03; // 3% estimation
    console.log('[StandardizedKPICalculatorService] 📊 Using estimated acquisition costs (3%):', estimatedCosts);
    return estimatedCosts;
  }
  
  /**
   * Calculate total annual operating expenses
   */
  private calculateAnnualOperatingExpenses(propertyData: UnifiedPropertyFinancials): number {
    return propertyData.propertyTax.amount +
           propertyData.insurance.amount +
           propertyData.maintenance.amount +
           propertyData.managementFee.amount +
           propertyData.utilities.amount +
           propertyData.otherExpenses.amount;
  }
  
  /**
   * Calculate total annual debt service for a specific year using loan table data
   * This provides accurate partial year calculations and real amortization values
   */
  private calculateAnnualDebtServiceForYear(loans: Loan[], targetYear: number): number {
    console.log(`[StandardizedKPICalculatorService] 💳 Calculating debt service for year ${targetYear}:`, {
      loanCount: loans?.length || 0,
      targetYear
    });
    
    if (!loans || loans.length === 0) {
      return 0;
    }
    
    let totalDebtServiceForYear = 0;
    
    loans.forEach(loan => {
      // Extract yearly payments using the new method
      const yearlyPayments = this.dataNormalizationService.extractYearlyLoanPayments(loan);
      const paymentForYear = yearlyPayments[targetYear] || 0;
      
      console.log(`[StandardizedKPICalculatorService] 💰 Loan ${loan.loanUID} payment for ${targetYear}: €${paymentForYear}`);
      totalDebtServiceForYear += paymentForYear;
    });
    
    console.log(`[StandardizedKPICalculatorService] 🎯 Total debt service for year ${targetYear}: €${totalDebtServiceForYear}`);
    
    return totalDebtServiceForYear;
  }

  /**
   * Calculate total annual debt service (legacy method, kept for backward compatibility)
   */
  private calculateAnnualDebtService(loanData: UnifiedLoanFinancials[]): number {
    console.log('[StandardizedKPICalculatorService] 💳 Calculating annual debt service:', {
      loanCount: loanData.length,
      loanDetails: loanData.map(loan => ({
        loanId: loan.loanId,
        originalAmount: loan.originalAmount.amount,
        regularPayment: {
          amount: loan.regularPayment.amount,
          period: loan.regularPayment.period
        },
        convertedAnnual: this.convertToAnnualAmount(loan.regularPayment)
      }))
    });
    
    const totalDebtService = loanData.reduce((total, loan) => {
      const annualPayment = this.convertToAnnualAmount(loan.regularPayment);
      console.log(`[StandardizedKPICalculatorService] 💰 Loan ${loan.loanId}: ${loan.regularPayment.amount} ${loan.regularPayment.period} = ${annualPayment} annually`);
      return total + annualPayment;
    }, 0);
    
    console.log('[StandardizedKPICalculatorService] 🎯 Total annual debt service:', totalDebtService);
    
    return totalDebtService;
  }
  
  /**
   * Generate cashflow summary
   */
  private generateCashflowSummary(cashflowRows: CashflowRow[]): CashflowSummary {
    const operationalRows = cashflowRows.slice(1); // Exclude year 0
    
    const totalCashInflow = operationalRows.reduce((sum, row) => sum + row.cashInflows.total, 0);
    const totalCashOutflow = operationalRows.reduce((sum, row) => sum + row.cashOutflows.total, 0);
    const totalNetCashflow = totalCashInflow - totalCashOutflow;
    const averageAnnualCashflow = operationalRows.length > 0 ? totalNetCashflow / operationalRows.length : 0;
    
    // Find breakeven year (when cumulative cashflow turns positive)
    const breakevenYear = cashflowRows.find(row => row.year > 0 && row.cumulativeCashflow > 0)?.year;
    
    return {
      totalCashInflow,
      totalCashOutflow,
      totalNetCashflow,
      averageAnnualCashflow,
      breakevenYear,
      irr: undefined // TODO: Calculate IRR using financial formulas
    };
  }

  /**
   * Generate acquisition cashflow row (Year 0)
   */
  private generateAcquisitionCashflowRow(
    propertyData: UnifiedPropertyFinancials,
    loanData: UnifiedLoanFinancials[],
    acquisitionYear: number,
    rawProperty?: Property
  ): CashflowRow {
    
    const purchasePrice = propertyData.purchasePrice.amount;
    const acquisitionCosts = this.calculateAcquisitionCosts(propertyData, rawProperty);
    const loanProceeds = loanData.reduce((sum, loan) => sum + loan.originalAmount.amount, 0);
    const totalCashOut = purchasePrice + acquisitionCosts;
    const equityContribution = totalCashOut - loanProceeds;
    
    // Extract detailed acquisition cost breakdown
    const acquisitionBreakdown = rawProperty?.financials?.purchaseInfo ? {
      brokerCommission: rawProperty.financials.purchaseInfo.brokerCommission || 0,
      legalFees: rawProperty.financials.purchaseInfo.legalFees || 0,
      otherFees: rawProperty.financials.purchaseInfo.otherFees || 0,
      hasRealData: (rawProperty.financials.purchaseInfo.brokerCommission || 0) + 
                   (rawProperty.financials.purchaseInfo.legalFees || 0) + 
                   (rawProperty.financials.purchaseInfo.otherFees || 0) > 0
    } : {
      brokerCommission: 0,
      legalFees: 0, 
      otherFees: acquisitionCosts, // Put estimated costs in "other fees"
      hasRealData: false
    };
    
    console.log('[StandardizedKPICalculatorService] 🏦 Acquisition row (real data):', {
      acquisitionYear,
      purchasePrice,
      acquisitionCosts,
      acquisitionBreakdown,
      loanProceeds,
      totalCashOut,
      equityContribution
    });
    
    return {
      year: 0,
      yearLabel: 'ACQUISITION',
      cashInflows: {
        loanProceeds,
        other: 0,
        total: loanProceeds
      },
      cashOutflows: {
        purchasePrice,
        acquisitionCosts,
        other: 0,
        total: totalCashOut,
        // Add acquisition-specific breakdown
        acquisitionBreakdown: {
          purchasePrice,
          brokerCommission: acquisitionBreakdown.brokerCommission,
          legalFees: acquisitionBreakdown.legalFees,
          otherFees: acquisitionBreakdown.otherFees,
          totalAcquisitionCosts: acquisitionCosts,
          hasRealData: acquisitionBreakdown.hasRealData
        }
      },
      netCashflow: loanProceeds - totalCashOut, // Usually negative (equity contribution)
      cumulativeCashflow: 0, // Will be calculated later
      notes: `Real data: Equity Contribution: €${equityContribution.toLocaleString()}`
    };
  }

  /**
   * Generate cashflow row using REAL DATA AGGREGATION
   * This method leverages existing proven services for accurate data
   */
  private generateRealDataCashflowRow(
    propertyData: UnifiedPropertyFinancials,
    loanData: UnifiedLoanFinancials[],
    yearOffset: number,
    calendarYear: number,
    period: TimeDatePeriod,
    rawLoans?: Loan[],
    rawContracts?: Contract[],
    _rawProperty?: Property
  ): CashflowRow {
    
    console.log(`[StandardizedKPICalculatorService] 📊 Generating real data row for ${calendarYear}:`, {
      yearOffset,
      calendarYear,
      hasRawLoans: !!rawLoans,
      rawLoanCount: rawLoans?.length || 0,
      hasRawContracts: !!rawContracts,
      rawContractCount: rawContracts?.length || 0
    });
    
    // ========================================================================
    // CRITICAL: Calculate pro-rata factor for purchase year using centralized helper
    // ========================================================================
    let proRataFactor = 1.0; // Default to full year
    if (propertyData.purchaseDate) {
      proRataFactor = this.dataNormalizationService.calculateProRataFactorForYear(
        propertyData.purchaseDate,
        calendarYear
      );
      
      if (proRataFactor < 1.0) {
        console.log(`[StandardizedKPICalculatorService] 🔧 Applying pro-rata factor ${proRataFactor.toFixed(3)} for purchase year ${calendarYear}`);
      }
    }
    
    // === CASH IN: Use real contract data where possible ===
    let effectiveGrossIncome: number;
    let dataSource: string;
    
    if (rawContracts && rawContracts.length > 0) {
      // Use real contract payment data (already handles contract overlap correctly)
      const contractIncome = this.extractRealContractIncome(rawContracts, period);
      effectiveGrossIncome = contractIncome.totalIncome;
      dataSource = contractIncome.hasRealData ? 'real' : 'projected';
      
      console.log(`[StandardizedKPICalculatorService] 💰 Contract income for ${calendarYear}: €${effectiveGrossIncome} (${dataSource})`);
    } else {
      // Fallback to projected income with growth AND pro-rata
      const baseRent = this.convertToAnnualAmount(propertyData.baseRent);
      const projectedRent = baseRent * Math.pow(1 + (propertyData.rentGrowthRate / 100), yearOffset);
      const vacancyLoss = projectedRent * (propertyData.vacancyRate / 100);
      effectiveGrossIncome = (projectedRent - vacancyLoss) * proRataFactor; // Apply pro-rata
      dataSource = 'projected';
      
      console.log(`[StandardizedKPICalculatorService] 📈 Projected income for ${calendarYear}: €${effectiveGrossIncome} (pro-rata: ${proRataFactor.toFixed(3)})`);
    }
    
    // === CASH OUT: Use real loan data where possible ===
    let totalDebtService: number;
    
    // Real loan payments using proven LoanAdapterService
    if (rawLoans && rawLoans.length > 0) {
      const debtServiceResult = this.loanAdapterService.calculateDebtService(rawLoans, period);
      totalDebtService = debtServiceResult.totalDebtService;
      
      console.log(`[StandardizedKPICalculatorService] 🏦 Real debt service for ${calendarYear}: €${totalDebtService} from ${rawLoans.length} loans`);
    } else {
      // Fallback to legacy calculation WITH pro-rata
      totalDebtService = this.calculateAnnualDebtService(loanData) * proRataFactor;
      console.log(`[StandardizedKPICalculatorService] 🏦 Legacy debt service for ${calendarYear}: €${totalDebtService} (pro-rata: ${proRataFactor.toFixed(3)})`);
    }
    
    // Operating expenses (with growth adjustment AND pro-rata)
    const baseOperatingExpenses = this.calculateAnnualOperatingExpenses(propertyData);
    const growthFactor = Math.pow(1 + (propertyData.expenseGrowthRate / 100), yearOffset);
    const operatingExpenses = baseOperatingExpenses * growthFactor * proRataFactor; // Apply pro-rata
    
    const totalCashOut = operatingExpenses + totalDebtService;
    const netCashflow = effectiveGrossIncome - totalCashOut;
    
    console.log(`[StandardizedKPICalculatorService] 📊 ${calendarYear} summary:`, {
      effectiveGrossIncome,
      operatingExpenses,
      totalDebtService,
      totalCashOut,
      netCashflow,
      proRataFactor: proRataFactor.toFixed(3),
      dataSource
    });
    
    return {
      year: yearOffset + 1,
      yearLabel: `${calendarYear}`,
      cashInflows: {
        rentalIncome: effectiveGrossIncome,
        other: 0,
        total: effectiveGrossIncome
      },
      cashOutflows: {
        operatingExpenses,
        debtService: totalDebtService,
        taxes: 0, // To be calculated based on tax strategy
        other: 0,
        total: totalCashOut,
        breakdown: {
          // ✅ CRITICAL FIX: Apply pro-rata to individual expense breakdowns for purchase year
          propertyTax: propertyData.propertyTax.amount * growthFactor * proRataFactor,
          insurance: propertyData.insurance.amount * growthFactor * proRataFactor,
          maintenance: propertyData.maintenance.amount * growthFactor * proRataFactor,
          managementFee: propertyData.managementFee.amount * growthFactor * proRataFactor,
          utilities: propertyData.utilities.amount * growthFactor * proRataFactor,
          otherExpenses: propertyData.otherExpenses.amount * growthFactor * proRataFactor,
          totalOperatingExpenses: operatingExpenses,
          totalLoanPayments: totalDebtService
        }
      },
      netCashflow,
      cumulativeCashflow: 0, // Will be calculated later
      notes: `${dataSource === 'real' ? 'Real data' : 'Projected'}: Rental income based on ${dataSource} contract data${proRataFactor < 1.0 ? ` (Pro-rated: ${(proRataFactor * 100).toFixed(0)}%)` : ''}`
    };
  }

  /**
   * Extract real contract income from actual contract payment data
   */
  private extractRealContractIncome(
    contracts: Contract[], 
    period: TimeDatePeriod
  ): { totalIncome: number; hasRealData: boolean } {
    
    let totalIncome = 0;
    let hasRealData = false;
    
    console.log(`[StandardizedKPICalculatorService] 📋 Extracting contract income for period ${period.startDate.getFullYear()}:`);
    
    contracts.forEach(contract => {
      if (contract.status !== 'Active') {
        return;
      }

      // Track whether THIS contract was already counted from its planned
      // payments, so the monthly-base-rent fallback is decided PER-CONTRACT
      // (previously gated on a shared flag → a second contract without planned
      // payments was silently dropped, under-counting multi-contract income).
      let contractCountedFromPlanned = false;

      // Check for planned payments in this period
      if (contract.plannedPayments && contract.plannedPayments.length > 0) {
        const periodPayments = contract.plannedPayments.filter(payment => {
          if (payment.terminated || payment.paid) return false;
          
          try {
            const paymentDate = new Date(payment.date);
            return paymentDate >= period.startDate && paymentDate <= period.endDate;
          } catch (e) {
            console.error(`[StandardizedKPICalculatorService] Invalid payment date:`, e);
            return false;
          }
        });
        
        if (periodPayments.length > 0) {
          const contractIncome = periodPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
          totalIncome += contractIncome;
          hasRealData = true;
          contractCountedFromPlanned = true;
        }
      }
      
      // Fallback to monthly base rent only if THIS contract had no counted
      // planned payments for the period (per-contract decision).
      if (!contractCountedFromPlanned && contract.contractFinancials?.monthlyBaseRent) {
        // FIXED: Calculate income based on actual contract period within the calendar year
        const periodYear = period.startDate.getFullYear();
        const contractStartDate = contract.contractDates?.startDate ? new Date(contract.contractDates.startDate) : null;
        const contractEndDate = contract.contractDates?.endDate ? new Date(contract.contractDates.endDate) : null;
        
        if (contractStartDate) {
          // Calculate calendar year overlap
          const yearStart = new Date(periodYear, 0, 1);
          const yearEnd = new Date(periodYear, 11, 31, 23, 59, 59);
          
          const overlapStart = new Date(Math.max(contractStartDate.getTime(), yearStart.getTime()));
          const overlapEnd = contractEndDate ? 
            new Date(Math.min(contractEndDate.getTime(), yearEnd.getTime())) : 
            yearEnd;
          
          if (overlapStart <= overlapEnd) {
            // Calculate months of overlap
            const startMonth = overlapStart.getMonth();
            const startYear = overlapStart.getFullYear();
            const endMonth = overlapEnd.getMonth();
            const endYear = overlapEnd.getFullYear();
            
            const monthsInYear = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
            const contractIncome = contract.contractFinancials.monthlyBaseRent * monthsInYear;
            
            totalIncome += contractIncome;
            
            console.log(`[StandardizedKPICalculatorService] 📝 Contract ${contract.contractUID}: €${contractIncome} from ${monthsInYear} months of overlap (${overlapStart.toISOString().slice(0,7)} to ${overlapEnd.toISOString().slice(0,7)})`);
          }
        } else {
          // No start date available, use full year as fallback
          const annualRent = contract.contractFinancials.monthlyBaseRent * 12;
          totalIncome += annualRent;
          console.log(`[StandardizedKPICalculatorService] 📝 Contract ${contract.contractUID}: €${annualRent} from monthly base rent (no start date - full year fallback)`);
        }
      }
    });
    
    console.log(`[StandardizedKPICalculatorService] 💰 Total contract income: €${totalIncome}, hasRealData: ${hasRealData}`);
    
    return { totalIncome, hasRealData };
  }
}

// Result interfaces
export interface PropertyKPIResults {
  // Income metrics
  grossAnnualIncome: number;
  actualGrossAnnualIncome: number; // Actual income from contracts (0 if vacant)
  effectiveGrossIncome: number;
  netOperatingIncome: number;
  vacancyLoss: number;
  netCashFlow: number;
  
  // Performance metrics
  grossYield: number;
  netYield: number;
  capRate: number;
  cashOnCashReturn: number;
  
  // Expense and debt metrics
  expenseRatio: number;
  breakevenRatio: number;
  dscr: number;
  ltv: number;
  
  // Financial structure
  totalOperatingExpenses: number;
  totalDebtService: number;
  totalLoanBalance: number;
  equity: number;
  
  // Metadata
  isRentEstimated: boolean; // True if rent is estimated from property value
  dataQuality: 'high' | 'medium' | 'low';
  timestamp: Date;
  calculatedBy: string;
}

export interface YearlyCashflowTable {
  propertyId: string;
  projectionYears: number;
  cashflowRows: CashflowRow[];
  summary: CashflowSummary;
  generatedAt: Date;
  calculatedBy: string;
}

export interface CashflowRow {
  year: number;
  yearLabel: string;
  cashInflows: {
    rentalIncome?: number;
    loanProceeds?: number;
    other: number;
    total: number;
  };
  cashOutflows: {
    purchasePrice?: number;
    acquisitionCosts?: number;
    operatingExpenses?: number;
    debtService?: number;
    taxes?: number;
    other: number;
    total: number;
    breakdown?: {
      propertyTax: number;
      insurance: number;
      maintenance: number;
      managementFee: number;
      utilities: number;
      otherExpenses: number;
      totalOperatingExpenses: number;
      totalLoanPayments: number;
    };
    acquisitionBreakdown?: {
      purchasePrice: number;
      brokerCommission: number;
      legalFees: number;
      otherFees: number;
      totalAcquisitionCosts: number;
      hasRealData: boolean;
    };
  };
  netCashflow: number;
  cumulativeCashflow: number;
  notes?: string;
}

export interface CashflowSummary {
  totalCashInflow: number;
  totalCashOutflow: number;
  totalNetCashflow: number;
  averageAnnualCashflow: number;
  breakevenYear?: number;
  irr?: number;
} 
